import { useQuery } from '@apollo/client';
import { useParams, useLocation, useNavigate, Redirect } from '@reach/router';
import type { ReactNode } from 'react';
import type React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { Pagination } from '@xing-com/crate-people-directory-pagination';
import { UserChunk } from '@xing-com/crate-people-directory-user-chunk';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';
import { Row, Column } from '@xing-com/xing-grid';

import {
  USER_BAG_FILTERS_ID,
  SEO_URL_META_DATA,
  userBagCityRoutes,
} from './constants';
import type {
  PeopleByCityUserBagPageQuery,
  PeopleByCityUserBagPageQueryVariables,
} from './graphql/user-bag-page-by-city.query.gql-types';
import { PeopleByCityUserBagPageDocument } from './graphql/user-bag-page-by-city.query.gql-types';
import { sanitizeNames } from './helper';
import {
  StyledHeadline,
  ContentRow,
  StyledFiltersForm,
} from './user-bag-page-by-city.styles';

const BASE_URL = `/people/cities/`;

// Slug can be "bauer-michael" or "bauer-michael-p2"
const SLUG_AND_PAGE_REGEX =
  /((?<slugAndPageSlug>[a-zA-Z]+-[a-zA-Z]+)-p(?<pageFromUrl>[0-9]+))|((?<singleSlug>[a-zA-Z]+-[a-zA-Z]+))/;

export const UserBagPageByCity: React.FC = () => {
  const { country, city: citySlug, citySlugOrLetter } = useParams();
  const { slugAndPageSlug, pageFromUrl, singleSlug } =
    citySlugOrLetter.match(SLUG_AND_PAGE_REGEX).groups;
  const slug = slugAndPageSlug || singleSlug || '';
  const pageFromSlug = pageFromUrl || 1;
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const currentFilters = {
    jobTitle: searchParams.get('jobtitle_query') || '',
  };

  const { data, error, loading } = useQuery<
    PeopleByCityUserBagPageQuery,
    PeopleByCityUserBagPageQueryVariables
  >(PeopleByCityUserBagPageDocument, {
    variables: {
      slug,
      city: citySlug.toLowerCase(),
      country,
      page: +pageFromSlug || 1,
      jobtitleQuery: currentFilters.jobTitle,
    },
    skip: !slug,
  });
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const onFiltersFormSubmit = (filters: Record<string, string>): void => {
    const searchParams = {
      ...(filters.jobTitle && { jobtitle_query: filters.jobTitle }),
    };

    navigate(
      `${BASE_URL}${country}/${city}/${citySlugOrLetter}?jobtitle_query=${searchParams.jobtitle_query}`
    );
  };

  const defaultPathParams = {
    city: citySlug.toLowerCase(),
    country,
    slug,
  };
  const letter = slug[0].toLowerCase();

  if (error || (!loading && !data)) {
    return (
      <Redirect to={`/people/cities/${country}/${citySlug.toLowerCase()}/`} />
    );
  }

  const { names, name, city, totalPages, page, count, breadcrumbs } =
    data?.peopleByCityUserBagPage || {};

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  const trackingContextData = currentFilters.jobTitle
    ? {
        PropSearchTerm: currentFilters.jobTitle,
      }
    : {};
  const seoTitle = formatMessage(
    { id: 'PD_CITIES_USER_BAG_PAGETITLE' },
    { display_name: name, city, total_count: count }
  );
  const seoDescription = formatMessage(
    { id: 'PD_CITIES_USER_BAG_META_DESCRIPTION' },
    { display_name: name, count, city }
  );
  const fullPageLayoutProps = {
    seoTitle,
    seoDescription,
    trackingContextData,
    index: true,
    seoUrl: `${SEO_URL_META_DATA}${slug}${page === 1 ? '' : `p-${page}`}`,
    pageName: `/user_bag/cities/${country}/${citySlug.toLowerCase()}/${letter}`,
    hidePageHeader: true,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  const filtersFormParams = [
    {
      id: USER_BAG_FILTERS_ID.jobTitle,
      labelKey: 'PD_USER_BAG_SEARCH_OCCUPATION_LABEL',
      helperTextKey: 'PD_USER_BAG_SEARCH_OCCUPATION_INPUT_PLACEHOLDER',
      value: currentFilters.jobTitle,
    },
  ];
  const filtersForm = (
    <StyledFiltersForm
      filters={filtersFormParams}
      onSubmit={onFiltersFormSubmit}
      submitLabelKey="PD_USER_BAG_SEARCH_SUBMIT_BUTTON"
    />
  );

  const headlineMessage = (
    <FormattedMessage
      id="PD_CITY_USER_BAG_HEADLINE"
      values={{ count, display_name: name, city }}
    />
  );
  const HeadlineRow = ({ children }: { children?: ReactNode }): JSX.Element => (
    <Row>
      <Column sizeWide={12} size={0}>
        {children}
        {filtersForm}
      </Column>
    </Row>
  );

  if (!loading && (!data?.peopleByCityUserBagPage || !names || !names.length)) {
    if (page && page > 1) {
      return (
        <Redirect
          to={`/people/cities/${country}/${citySlug.toLowerCase()}/${slug}`}
        />
      );
    } else {
      return (
        <PageLayout {...fullPageLayoutProps}>
          <HeadlineRow />
          <UserMessage type={UserMessageType.emptyUserBags} />
        </PageLayout>
      );
    }
  }

  return (
    <PageLayout {...fullPageLayoutProps}>
      {isDesktop ? (
        <HeadlineRow>
          <StyledHeadline size="xxlarge">{headlineMessage}</StyledHeadline>
        </HeadlineRow>
      ) : null}
      <ContentRow $isLoading={loading}>
        <Column size={12}>
          {!isDesktop ? (
            <>
              <StyledHeadline size="large">{headlineMessage}</StyledHeadline>
              {filtersForm}
            </>
          ) : null}
          {names ? (
            <UserChunk names={sanitizeNames(names)} fullWidthLayout />
          ) : null}
        </Column>
        <Column size={12}>
          {slug && page && totalPages && totalPages > 1 && (
            <Pagination
              currentPage={page}
              pagesNumber={totalPages || 0}
              route={userBagCityRoutes.USER_BAG_PAGE_PAGINATED}
              routeFirstPage={userBagCityRoutes.USER_BAG_PAGE}
              routeParams={defaultPathParams}
              filterQuery={currentFilters.jobTitle ? currentFilters : {}}
            />
          )}
        </Column>
      </ContentRow>
    </PageLayout>
  );
};
